import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { Router, NavigationStart, NavigationError } from '@angular/router';
import { HttpService } from './services/http.service';
import { UserService } from './services/user.service';
import { environment } from '../environments/environment';
import { IUser } from '@interfaces/user';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { LicensesComponent } from '@modules/shared/dialogs/licenses/licenses.component';
import { IAnnouncement } from '@interfaces/announcement';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnnouncementComponent } from '@modules/shared/dialogs/announcement/announcement.component';
import { TranslateService } from '@services/translate.service';
import { TranslateInputValuePipe } from '@modules/shared/pipes/translate-input-value.pipe';
import { MatIconRegistry } from '@angular/material/icon';
import { ThemeService } from '@services/theme.service';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false,
})
export class AppComponent implements OnInit {
  isIframe = false;

  @ViewChild('sideNav') sideNav: ElementRef;

  sideNavVisible = false;

  // Router loading animation check
  routerLoading = false;

  // Http loading animation check
  httpLoading = false;

  user: IUser;

  // Current Flow version
  version: string;

  development = false;

  state = 'initial';

  constructor(
    private router: Router,
    public http: HttpService,
    public userService: UserService,
    private dialog: MatDialog,
    private msalBroadcastService: MsalBroadcastService,
    public msalService: MsalService,
    public snackBar: MatSnackBar,
    public translateService: TranslateService,
    public translateInputValuePipe: TranslateInputValuePipe,
    public matIconRegistry: MatIconRegistry,
    public themeService: ThemeService,
  ) {
    this.isIframe = window !== window.parent && !window.opener;
    if (this.isIframe) {
      window.document.querySelector('body').classList.add('is-iframe');
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }

    // Use material symbols instead of icons
    // https://github.com/angular/components/issues/24845
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');

    // Handle loading animation with navigation events
    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          // this.routerLoading = true;
          this.sideNavVisible = false;
          break;
        }

        /*case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.routerLoading = false;
          break;
        }*/

        case event instanceof NavigationError: {
          if (event.error?.message?.includes('Failed to fetch dynamically imported module')) {
            // Sentry.captureException(new Error('NavigationError/Failed to fetch dynamically imported module'));
            // Reload the page when the app tries to load a module that is not available anymore
            location.reload();
          }

          break;
        }

        default: {
          break;
        }
      }
    });

    if (!environment.production || window.location.hostname.includes('-dev.')) {
      this.development = true;
      window.document.title = '(DEV) ' + window.document.title;
    }
  }

  reload() {
    // Reload the page
    window.location.reload();
  }

  updateAnnouncements() {
    this.http
      .get('/announcements/visible')
      .then((announcements: IAnnouncement[]) => {
        announcements.forEach((announcement) => {
          // Check if announcement has been shown before or if it's updated after last shown
          const announcements: Record<string, string> = JSON.parse(localStorage.getItem('announcements')) || {};
          if (
            announcements[announcement._id] &&
            dayjs(announcement.updatedAt).isSameOrBefore(dayjs(announcements[announcement._id]))
          ) {
            return;
          }

          // Show snackbar
          const snackBarRef = this.snackBar.open(
            this.translateInputValuePipe.transform(announcement, 'name'),
            /** t(showAnnouncement) */
            this.translateService.translate('showAnnouncement'),
            {
              duration: 0,
              panelClass: 'snackbar-' + announcement.type,
            },
          );

          // Open announcement dialog on action
          snackBarRef.onAction().subscribe(() => {
            // Save announcement to localStorage
            const announcements: Record<string, string> = JSON.parse(localStorage.getItem('announcements')) || {};
            announcements[announcement._id] = announcement.updatedAt;
            localStorage.setItem('announcements', JSON.stringify(announcements));

            this.dialog.open(AnnouncementComponent, {
              data: announcement,
            });
          });
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  ngOnInit() {
    console.log(`Navigator language: ${navigator.language}`);

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(async () => {
        if (this.state === 'initial' || this.state === 'loadUserManually') {
          try {
            this.state = 'loadUser';

            await this.userService.loadUser();

            this.state = null;

            // Update announcements after 15 seconds
            setTimeout(() => this.updateAnnouncements(), 15000);

            // After that update once every 5 minutes
            setInterval(() => this.updateAnnouncements(), 300000);
          } catch (e) {
            setTimeout(() => {
              this.state = 'loadUserManually';
            }, 5000);
          }
        }
      });
  }

  formatUserName(name: string) {
    return name.match(/\b\w/g).join('');
  }

  toggleSidenav() {
    this.sideNavVisible = !this.sideNavVisible;
  }

  mainClicked() {
    this.sideNavVisible = false;
  }

  public getRouterOutletState(outlet) {
    return outlet.isActivated ? outlet.activatedRoute : '';
  }

  displayLicenses() {
    this.dialog.open(LicensesComponent);
  }
}
